import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ActionItem {
  action: string;
  priority: 'high' | 'medium' | 'low';
  timing: 'immediate' | 'this_week' | 'this_month';
}

export interface NewsIntelligenceRow {
  id: string;
  title: string;
  url: string | null;
  published_at: string | null;
  source_feed: string | null;
  summary: string | null;
  short_summary: string | null;
  executive_briefing: string | null;
  opportunity_analysis: string | null;
  match_type: string | null;
  relevance_score: number | null;
  priority_level: number | null;
  priority_label: string | null;
  signal_type: string | null;
  is_competitor_alert: boolean;
  is_prospect_opportunity: boolean;
  is_investor_activity: boolean;
  matched_keywords: string[] | null;
  prospect_company_name: string | null;
  prospect_confidence: string | null;
  linkedin_score: number | null;
  linkedin_post_draft: string | null;
  is_primary: boolean;
  also_covered_by: string | null;
  is_tier2: boolean;
  email_sent: boolean;
  email_sent_at: string | null;
  data_source: string;
  created_at: string | null;
  updated_at: string | null;
  source_article_id: string | null;
  source_intelligence_id: string | null;
  action_items: ActionItem[] | null;
  outreach_guidance: string | null;
  match_reasoning: string | null;
  matched_competitors: string[] | null;
  matched_accounts: string[] | null;
  matched_categories: string[] | null;
  suggested_contacts: unknown | null;
  enriched_content: string | null;
  market_context: string | null;
  companies_mentioned: string[] | null;
  enrichment_metadata: {
    why_it_matters?: string;
    recommended_action?: string;
    opportunity_type?: string;
  } | null;
  related_articles: unknown | null;
  enriched_at: string | null;
  analyzed_at: string | null;
  author: string | null;
  article_categories: string[] | null;
  feedback_status: string | null;
  feedback_notes: string | null;
  feedback_by: string | null;
  feedback_at: string | null;
}

export interface NewsIntelligenceStats {
  total_articles: number;
  competitor_alerts: number;
  prospect_opportunities: number;
  investor_activity: number;
  high_priority: number;
  medium_priority: number;
  low_priority: number;
  emails_sent: number;
  linkedin_worthy: number;
  from_biocreative: number;
  from_legacy: number;
  oldest_article: string | null;
  newest_article: string | null;
  last_7_days: number;
  last_30_days: number;
}

export interface NewsIntelligenceFilters {
  searchTerm?: string;
  priorityLevel?: number;
}

export type NewsFilterTab = 'all' | 'opportunities' | 'competitors' | 'priority';

const VIEW_MAP: Record<NewsFilterTab, string> = {
  all: 'v_news_intelligence_table',
  opportunities: 'v_news_intelligence_opportunities',
  competitors: 'v_news_intelligence_competitors',
  priority: 'v_news_intelligence_priority',
};

export const useNewsIntelligence = (
  tab: NewsFilterTab = 'all',
  filters?: NewsIntelligenceFilters,
  limit: number = 500
) => {
  return useQuery({
    queryKey: ['news-intelligence', tab, filters, limit],
    queryFn: async () => {
      const viewName = VIEW_MAP[tab];
      
      let query = (supabase as any)
        .from(viewName)
        .select('*')
        .order('published_at', { ascending: false, nullsFirst: false })
        .limit(limit);

      if (filters?.priorityLevel) {
        query = query.eq('priority_level', filters.priorityLevel);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching News Intelligence:', error);
        throw error;
      }

      let results = (data as NewsIntelligenceRow[]) || [];

      if (filters?.searchTerm) {
        const term = filters.searchTerm.toLowerCase();
        results = results.filter(item =>
          item.title?.toLowerCase().includes(term) ||
          item.summary?.toLowerCase().includes(term) ||
          item.source_feed?.toLowerCase().includes(term) ||
          item.executive_briefing?.toLowerCase().includes(term) ||
          item.matched_keywords?.some(k => k.toLowerCase().includes(term)) ||
          item.prospect_company_name?.toLowerCase().includes(term)
        );
      }

      return results;
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useNewsIntelligenceStats = () => {
  return useQuery({
    queryKey: ['news-intelligence-stats'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('v_news_intelligence_stats')
        .select('*')
        .single();

      if (error) {
        console.error('Error fetching News Intelligence stats:', error);
        throw error;
      }

      return data as NewsIntelligenceStats;
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useNewsIntelligenceTop = (limit: number = 5) => {
  return useQuery({
    queryKey: ['news-intelligence-top', limit],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('v_news_intelligence_table')
        .select('*')
        .gte('linkedin_score', 7)
        .order('linkedin_score', { ascending: false })
        .order('published_at', { ascending: false, nullsFirst: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching top News Intelligence:', error);
        throw error;
      }

      return (data as NewsIntelligenceRow[]) || [];
    },
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });
};

export const getTypeLabel = (matchType: string | null): string => {
  switch (matchType) {
    case 'potential_customer': return 'Opportunity';
    case 'manual_competitor': return 'Competitor';
    case 'keyword': return 'Keyword Match';
    default: return matchType || 'Unknown';
  }
};

export const getPriorityLabel = (level: number | null): string => {
  switch (level) {
    case 1: return 'High';
    case 2: return 'Medium';
    case 3:
    default: return 'Low';
  }
};

export const getImpactLevel = (linkedinScore: number | null): 'High' | 'Medium' | 'Low' => {
  if (!linkedinScore) return 'Low';
  if (linkedinScore >= 7) return 'High';
  if (linkedinScore >= 4) return 'Medium';
  return 'Low';
};
